import React, { useMemo, useState, useEffect } from "react";
import { DayPost, HealthLog } from "../../types/post";
import { generateDateID, getDayPost } from "../../services/dayPostService";
import { calcSleepFromTimeline, calcSleepFromWellnessLog, formatSleepDuration } from "../../utils";
import { useAuth } from "../../contexts/AuthContext";

interface SleepDaysProps {
  dayPosts: DayPost[];
  className?: string;
  targetHours?: number; // default 8
  latestHealthLog?: (HealthLog & { id: string }) | null;
}

const SleepDays: React.FC<SleepDaysProps> = ({
  dayPosts,
  className = "",
  targetHours = 8,
  latestHealthLog = null,
}) => {
  const { currentUser } = useAuth();

  // State for 28-day data fetching
  const [extended28DayData, setExtended28DayData] = useState<DayPost[]>([]);
  const [loading28DayData, setLoading28DayData] = useState(false);
  const [has28DayDataLoaded, setHas28DayDataLoaded] = useState(false);

  // Effect to fetch 28-day data when component mounts
  useEffect(() => {
    const fetch28DayData = async () => {
      if (!currentUser?.uid || loading28DayData || has28DayDataLoaded) return;

      setLoading28DayData(true);

      try {
        const today = new Date();
        const dateIds: string[] = [];

        // Generate last 28 days worth of dateIDs
        for (let i = 27; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          dateIds.push(generateDateID(date));
        }

        // Fetch data for dates that aren't already in dayPosts
        const existingDateIds = new Set(dayPosts.map((post) => post.dateID));
        const missingDateIds = dateIds.filter(
          (dateId) => !existingDateIds.has(dateId)
        );

        // console.log(
        //   `[SleepDays-28Day] Fetching ${missingDateIds.length} missing days for 28-day comparison`
        // );

        // Fetch missing days
        const missingDayPosts = await Promise.all(
          missingDateIds.map(async (dateId) => {
            try {
              const dayPost = await getDayPost(currentUser.uid, dateId);
              return dayPost;
            } catch (error) {
              // console.warn(
              //   `[SleepDays-28Day] Failed to fetch ${dateId}:`,
              //   error
              // );
              return null;
            }
          })
        );

        // Filter out nulls and combine with existing data
        const validMissingPosts = missingDayPosts.filter(
          (post): post is DayPost => post !== null
        );
        const combinedData = [...dayPosts, ...validMissingPosts];

        setExtended28DayData(combinedData);
        setHas28DayDataLoaded(true);

        // console.log(
        //   `[SleepDays-28Day] Successfully loaded ${validMissingPosts.length} additional days`
        // );
      } catch (error) {
        // console.error("[SleepDays-28Day] Error fetching 28-day data:", error);
        // Fallback to existing data
        setExtended28DayData(dayPosts);
        setHas28DayDataLoaded(true);
      } finally {
        setLoading28DayData(false);
      }
    };

    fetch28DayData();
  }, [currentUser?.uid, dayPosts, loading28DayData, has28DayDataLoaded]);

  // Helper function to get health log with sleep data
  const getHealthLogWithLongestSleep = (
    healthLogs: (HealthLog & { id: string })[]
  ) => {
    if (!healthLogs || healthLogs.length === 0) return null;
    // Use latest health log (last in array) for consistency with other components
    const latestLog = healthLogs[healthLogs.length - 1];
    return latestLog;
  };

  const { weekData, percentageDifference } = useMemo(() => {
    const today = new Date();
    const days: Array<{
      dateID: string;
      dayName: string;
      sleepHours: number; // hours
      isToday: boolean;
    }> = [];

    // Calculate last 7 days data
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      const dateID = generateDateID(date);
      const dayName = date
        .toLocaleDateString("en-US", { weekday: "narrow" })
        .toUpperCase();

      const dayPost = dayPosts.find((post) => post.dateID === dateID);
      const longestSleepHealth = dayPost
        ? getHealthLogWithLongestSleep(dayPost.healthLogs)
        : null;
      let sleepSeconds = longestSleepHealth
        ? calcSleepFromTimeline(
            longestSleepHealth.sleepTimeline,
            longestSleepHealth.sleepDuration
          )
        : 0;
      // Fallback to wellness log sleep if no health log sleep data
      if (sleepSeconds <= 0 && dayPost?.wellnessLogs?.length) {
        const latestWellness = dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1];
        if (latestWellness?.sleepTime) {
          sleepSeconds = calcSleepFromWellnessLog(latestWellness.sleepTime);
        }
      }
      const sleepHours = sleepSeconds > 0 ? sleepSeconds / 3600 : 0;

      days.push({
        dateID,
        dayName,
        sleepHours,
        isToday: dateID === generateDateID(today),
      });
    }

    // Calculate 28-day comparison using extended data when available
    const dataSourceForComparison = has28DayDataLoaded
      ? extended28DayData
      : dayPosts;
    let comparisonPeriodTotal = 0;
    let actualDaysFound = 0;

    // Always check 28 days when we have extended data
    const daysToCheck = has28DayDataLoaded
      ? 28
      : Math.min(28, dayPosts.length * 4);

    for (let i = daysToCheck - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateID = generateDateID(date);

      const dayPost = dataSourceForComparison.find(
        (post) => post.dateID === dateID
      );
      if (dayPost) {
        const longestSleepHealth = getHealthLogWithLongestSleep(
          dayPost.healthLogs
        );
        let sleepSeconds = longestSleepHealth
          ? calcSleepFromTimeline(
              longestSleepHealth.sleepTimeline,
              longestSleepHealth.sleepDuration
            )
          : 0;
        // Fallback to wellness log sleep if no health log sleep data
        if (sleepSeconds <= 0 && dayPost.wellnessLogs?.length) {
          const latestWellness = dayPost.wellnessLogs[dayPost.wellnessLogs.length - 1];
          if (latestWellness?.sleepTime) {
            sleepSeconds = calcSleepFromWellnessLog(latestWellness.sleepTime);
          }
        }
        if (sleepSeconds > 0) {
          actualDaysFound++;
          const sleepHours = sleepSeconds / 3600;
          comparisonPeriodTotal += sleepHours;
        }
      }
    }

    // Calculate averages
    const last7DaysTotal = days.reduce((sum, day) => sum + day.sleepHours, 0);
    const last7DaysCount = days.filter((day) => day.sleepHours > 0).length;

    const comparisonPeriodAverage =
      actualDaysFound > 0 ? comparisonPeriodTotal / actualDaysFound : 0;
    const last7DaysAverage =
      last7DaysCount > 0 ? last7DaysTotal / last7DaysCount : 0;

    // Calculate percentage difference
    const percentDiff = (() => {
      // If no sleep data in either period, show 0%
      if (comparisonPeriodAverage === 0 && last7DaysAverage === 0) return 0;

      // If we don't have enough comparison data, show a simple indicator
      if (actualDaysFound < 7) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If baseline is too small, don't show percentage
      const minThreshold = 1; // 1 hour minimum
      if (comparisonPeriodAverage < minThreshold) {
        if (last7DaysAverage > comparisonPeriodAverage) return 100;
        if (last7DaysAverage < comparisonPeriodAverage) return -100;
        return 0;
      }

      // If no recent sleep but baseline exists, show as negative change
      if (last7DaysAverage === 0) return -100;

      // Normal calculation with cap at ±200%
      const rawPercent =
        ((last7DaysAverage - comparisonPeriodAverage) /
          comparisonPeriodAverage) *
        100;
      return Math.max(-200, Math.min(200, rawPercent));
    })();

    // Debug logging
    // console.log(`[SleepDays] Calculation Summary:`);
    // console.log(`  Available dayPosts: ${dayPosts.length}`);
    // console.log(`  Extended 28-day data loaded: ${has28DayDataLoaded}`);
    // console.log(`  Days found in comparison period: ${actualDaysFound}`);
    // console.log(`  7-day count: ${last7DaysCount}`);
    // console.log(`  7-day average: ${last7DaysAverage.toFixed(1)} hours`);
    // console.log(
    //   `  Comparison period average: ${comparisonPeriodAverage.toFixed(1)} hours`
    // );
    // console.log(`  Percentage difference: ${percentDiff.toFixed(1)}%`);

    return {
      weekData: days,
      percentageDifference: percentDiff,
    };
  }, [dayPosts, has28DayDataLoaded, extended28DayData]);

  const targetDaysCount = weekData.filter(
    (d) => d.sleepHours >= targetHours
  ).length;

  // Calculate today's sleep - use weekData which already includes wellness fallback
  const todayEntry = weekData.find((d) => d.isToday);
  const todaysSleep = todayEntry && todayEntry.sleepHours > 0
    ? todayEntry.sleepHours * 3600 // convert back to seconds for formatSleepDuration
    : latestHealthLog
      ? calcSleepFromTimeline(
          latestHealthLog.sleepTimeline,
          latestHealthLog.sleepDuration
        )
      : null;

  return (
    <div className={`bg-base-100 rounded-lg ${className}`}>
      <div className="flex items-center justify-between">
        {/* Left summary - Days goal hit and comparison */}
        <div className="flex-1">
          <h3 className="text-sm font-heading">
            Sleep{" "}
            <span className="font-grotesk text-base-content/60 font-light">Goals</span>
          </h3>
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-4xl font-bold font-heading text-base-content">
              {targetDaysCount}
            </span>
            <span className="text-3xl font-light text-base-content/50">of 7</span>

            {/* Percentage difference indicator */}
            <span
              className={`text-sm font-medium px-2 py-1 rounded-md ${
                percentageDifference > 0
                  ? "bg-purple-500/15 text-purple-700"
                  : percentageDifference < 0
                  ? "bg-base-200 text-base-content/80"
                  : "bg-base-200 text-base-content/80"
              }`}
            >
              {percentageDifference > 0 ? "+" : ""}
              {Math.round(percentageDifference)}%
            </span>
          </div>

          {/* Today's Sleep */}
          {todaysSleep && (
            <div>
              <div className="text-xs text-base-content/60">
                Today's Sleep{" "}
                <strong>{formatSleepDuration(todaysSleep)}</strong>
              </div>
            </div>
          )}
        </div>

        {/* Right visualization */}
        <div className="flex flex-col items-center mt-2">
          <div className="flex space-x-1 items-end">
            {weekData.map((day) => {
              const ratio = Math.min(day.sleepHours / targetHours, 1);
              const heightPct = Math.round(ratio * 100);
              const hitTarget = day.sleepHours >= targetHours;

              return (
                <div key={day.dateID} className="w-4 h-12 relative">
                  <div className="absolute inset-0 rounded-full bg-base-300" />
                  <div
                    className={`absolute bottom-0 left-0 right-0 rounded-full ${
                      hitTarget ? "bg-purple-600" : "bg-purple-200"
                    }`}
                    style={{ height: hitTarget ? "100%" : `${heightPct}%` }}
                  />
                  {hitTarget && (
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Day labels */}
          <div className="flex space-x-1">
            {weekData.map((day) => (
              <div key={`${day.dateID}-label`} className="w-4 text-center">
                <span
                  className={`text-xs font-medium ${
                    day.isToday
                      ? "text-base-content font-semibold"
                      : "text-base-content/60"
                  }`}
                >
                  {day.dayName}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SleepDays;
